import React from 'react'
import { Button, Heading, Text, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import type { SupportCenterHoliday } from '../../types/supportCenterSpecialties'
import { ArrowPathIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { buildIsoDate, formatDateBr, parseIsoDate } from '../../components/centralAmparo/scheduleHelpers'
import {
  MODULE_NAME,
  clampDay,
  defaultUnavailabilityPeriodRange,
  getErrorMessage,
  periodsOverlap,
  todayParts,
} from '../../components/centralAmparo/parametrosHelpers'
import {
  RecordListPagination,
  sliceRecordPage,
  useRecordListPage,
} from '../../components/centralAmparo/RecordListPagination'
import ParametrosHolidayModal, { type HolidayModalState } from './ParametrosHolidayModal'
import ParametrosDeleteModal from './ParametrosDeleteModal'

const filterInputClass =
  'rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

const ParametrosFeriadosTab: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')

  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [holidays, setHolidays] = React.useState<SupportCenterHoliday[]>([])

  const defaultRange = React.useMemo(() => defaultUnavailabilityPeriodRange(), [])
  const [periodFrom, setPeriodFrom] = React.useState(defaultRange.from)
  const [periodTo, setPeriodTo] = React.useState(defaultRange.to)

  const [holidayModal, setHolidayModal] = React.useState<HolidayModalState | null>(null)
  const [submittingHoliday, setSubmittingHoliday] = React.useState(false)
  const [holidayError, setHolidayError] = React.useState<string | null>(null)

  const [deleteModal, setDeleteModal] = React.useState<{ id: string; label: string } | null>(null)
  const [deleteReason, setDeleteReason] = React.useState('')
  const [submittingDelete, setSubmittingDelete] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const rows = await supportCenterSpecialtiesService.listHolidays()
      setHolidays(rows)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const filteredHolidays = React.useMemo(
    () =>
      holidays
        .filter((h) => periodsOverlap(h.holiday_on, h.holiday_on, periodFrom, periodTo))
        .sort((a, b) => {
          const byDate = a.holiday_on.localeCompare(b.holiday_on)
          if (byDate !== 0) return byDate
          return a.name.localeCompare(b.name, 'pt-BR')
        }),
    [holidays, periodFrom, periodTo]
  )
  const [page, setPage] = useRecordListPage(`${periodFrom}|${periodTo}`)
  const recordPage = sliceRecordPage(filteredHolidays, page)

  const restorePeriod = () => {
    const next = defaultUnavailabilityPeriodRange()
    setPeriodFrom(next.from)
    setPeriodTo(next.to)
  }

  const openNewHoliday = () => {
    const date = todayParts()
    setHolidayError(null)
    setHolidayModal({
      name: '',
      date,
      all_day: true,
      hours: [],
      reason: '',
    })
  }

  const openEditHoliday = (item: SupportCenterHoliday) => {
    setHolidayError(null)
    setHolidayModal({
      id: item.id,
      name: item.name,
      date: parseIsoDate(item.holiday_on),
      all_day: item.all_day,
      hours: item.hours ?? [],
      reason: item.reason ?? '',
    })
  }

  const closeHolidayModal = () => {
    if (submittingHoliday) return
    setHolidayModal(null)
    setHolidayError(null)
  }

  const handleHolidaySave = async () => {
    if (!holidayModal || !user?.id) return
    if (!holidayModal.name.trim()) {
      setHolidayError('Informe o nome do feriado.')
      return
    }

    const day = clampDay(holidayModal.date)
    const holidayOn = buildIsoDate(day.year, day.month, day.day)

    if (!holidayModal.all_day && holidayModal.hours.length === 0) {
      setHolidayError('Selecione ao menos uma hora ou marque dia inteiro.')
      return
    }

    setSubmittingHoliday(true)
    setHolidayError(null)
    try {
      const savedId = await supportCenterSpecialtiesService.upsertHoliday({
        id: holidayModal.id,
        name: holidayModal.name,
        holiday_on: holidayOn,
        all_day: holidayModal.all_day,
        hours: holidayModal.hours,
        reason: holidayModal.reason,
        actor_id: user.id,
      })
      const dateLabel = formatDateBr(holidayOn)
      const name = holidayModal.name.trim()
      void (holidayModal.id
        ? logUpdate(
            'support_center_holiday',
            holidayModal.id,
            `Atualizou feriado: ${name} (${dateLabel})`
          )
        : logCreate(
            'support_center_holiday',
            savedId,
            `Criou feriado: ${name} (${dateLabel})`
          ))
      closeHolidayModal()
      await loadData()
    } catch (error) {
      setHolidayError(getErrorMessage(error))
    } finally {
      setSubmittingHoliday(false)
    }
  }

  const closeDeleteModal = () => {
    if (submittingDelete) return
    setDeleteModal(null)
    setDeleteReason('')
  }

  const confirmDelete = async () => {
    if (!deleteModal || !user?.id || !deleteReason.trim()) return
    setSubmittingDelete(true)
    try {
      await supportCenterSpecialtiesService.softDeleteHoliday(
        deleteModal.id,
        deleteReason.trim(),
        user.id
      )
      void logDelete(
        'support_center_holiday',
        deleteModal.id,
        `Excluiu feriado: ${deleteModal.label}`,
        { reason: deleteReason.trim() }
      )
      closeDeleteModal()
      await loadData()
    } catch (error) {
      setPageError(getErrorMessage(error))
      closeDeleteModal()
    } finally {
      setSubmittingDelete(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Feriados
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {filteredHolidays.length}
              {filteredHolidays.length !== holidays.length ? ` de ${holidays.length}` : ''}{' '}
              {filteredHolidays.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap flex-shrink-0">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="shrink-0">De</span>
              <input
                type="date"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                className={filterInputClass}
                aria-label="Período de"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="shrink-0">Até</span>
              <input
                type="date"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                className={filterInputClass}
                aria-label="Período até"
              />
            </label>
            <Button
              variant="outline"
              size="md"
              onClick={restorePeriod}
              title="Restaurar período (hoje → +60 dias)"
            >
              <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
              Restaurar período
            </Button>
            {canInsert && (
              <Button variant="solid" size="md" onClick={openNewHoliday}>
                <PlusIcon className="h-4 w-4" />
                Novo feriado
              </Button>
            )}
          </div>
        </div>

        {filteredHolidays.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              {holidays.length === 0
                ? 'Nenhum feriado cadastrado.'
                : 'Nenhum feriado no período selecionado.'}
            </Text>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Motivo
                  </th>
                  {(canUpdate || canDelete) && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {recordPage.items.map((item) => {
                  const dateLabel = formatDateBr(item.holiday_on)
                  const typeLabel = item.all_day
                    ? 'Dia inteiro'
                    : item.hours.map((h) => `${h.toString().padStart(2, '0')}h`).join(', ')
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {item.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {dateLabel}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {typeLabel}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {item.reason || '—'}
                      </td>
                      {(canUpdate || canDelete) && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {canUpdate && (
                              <button
                                type="button"
                                title="Editar"
                                onClick={() => openEditHoliday(item)}
                                className="text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                title="Excluir"
                                onClick={() => {
                                  setDeleteModal({
                                    id: item.id,
                                    label: `${item.name} (${dateLabel})`,
                                  })
                                  setDeleteReason('')
                                }}
                                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <RecordListPagination
            page={recordPage.page}
            totalPages={recordPage.totalPages}
            startIndex={recordPage.startIndex}
            endIndex={recordPage.endIndex}
            total={recordPage.total}
            onPageChange={setPage}
          />
          </>
        )}
      </div>

      {holidayModal && (
        <ParametrosHolidayModal
          holidayModal={holidayModal}
          holidayError={holidayError}
          submitting={submittingHoliday}
          onChange={setHolidayModal}
          onClose={closeHolidayModal}
          onSave={() => void handleHolidaySave()}
        />
      )}

      {deleteModal && (
        <ParametrosDeleteModal
          label={deleteModal.label}
          deleteReason={deleteReason}
          submitting={submittingDelete}
          onReasonChange={setDeleteReason}
          onClose={closeDeleteModal}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  )
}

export default ParametrosFeriadosTab
